// @ts-nocheck

import { computed } from 'vue'
import { sub as DateFnsSub, add as DateFnsAdd } from 'date-fns'
import useStores from '~/composables/useStores'

export const useShiftsLogic = (AuthStore) => {
  const { shifts: ShiftsStore } = useStores()

  const shift = computed(() => {
    if (!AuthStore.reactiveUser) return null

    const shift = ShiftsStore.findInStore({
      $or: [
        {
          startsAt: {
            $lte: new Date().getTime(),
          },
          endsAt: {
            $gte: new Date().getTime(),
          },
        },
        {
          startsAt: {
            $gte: new Date().getTime(),
          },
        },
      ],
      $and: [
        { [`staffLogs.${AuthStore.reactiveUser._id}.endedAt`]: null },
        { assignedStaffIds: AuthStore.reactiveUser._id },
      ],
      $sort: { startsAt: 1 },
    })[0]

    return shift?.value
  })

  const shiftIsHappening = computed(() => {
    if (!shiftsSupported.value || !shift.value || !AuthStore.reactiveUser)
      return

    const startTime = DateFnsSub(new Date(shift.value.startsAt), { hours: 1 })
    const endTime = DateFnsAdd(new Date(shift.value.endsAt), { hours: 1 })

    return (
      shift.value.assignedStaffIds.includes(AuthStore.reactiveUser._id) &&
      !shift.value.staffLogs?.[AuthStore.reactiveUser._id]?.endedAt &&
      startTime <= new Date() &&
      endTime >= new Date()
    )
  })

  // Staff with auto-shift never start/end manually — payroll counts their
  // scheduled window automatically, so we never prompt them to log in/out.
  const autoShift = computed(
    () => AuthStore.reactiveUser?.profile?.autoShift === true,
  )

  const shiftShouldBeStarted = computed(() => {
    if (
      autoShift.value ||
      !shift.value ||
      !AuthStore.reactiveUser ||
      !shift.value.staffLogs
    )
      return false

    return (
      shift.value.assignedStaffIds.includes(AuthStore.reactiveUser._id) &&
      AuthStore.reactiveUser._id in shift.value.staffLogs === false
    )
  })

  const shiftIsMissing = computed(() => {
    if (!shift.value || !AuthStore.reactiveUser || !shift.value.staffLogs)
      return false

    return !shift.value.assignedStaffIds.includes(AuthStore.reactiveUser._id)
  })

  const shiftShouldBeEnded = computed(() => {
    if (
      autoShift.value ||
      !shift.value ||
      !AuthStore.reactiveUser ||
      !shift.value.staffLogs
    )
      return false

    return (
      shift.value.assignedStaffIds.includes(AuthStore.reactiveUser._id) &&
      AuthStore.reactiveUser._id in shift.value.staffLogs &&
      shift.value.staffLogs[AuthStore.reactiveUser._id]?.startedAt &&
      !shift.value.staffLogs[AuthStore.reactiveUser._id]?.endedAt &&
      new Date(shift.value.startsAt).getTime() < new Date().getTime()
    )
  })

  const shiftsSupported = computed(() => {
    const profile = AuthStore.reactiveUser?.profile
    const correctRole =
      profile?.role &&
      (profile.role === 'cook' ||
        profile.role === 'barmen' ||
        profile.role === 'waiter' ||
        profile.role === 'deliveryman' ||
        profile.role === 'dishwasher')

    if (!correctRole) return

    // `useShifts` is Type.Optional in users.schema (made optional so
    // manager/app-owner/superadmin can be saved without the field —
    // see commit 29eb3d5e).  Truthy check turned "field missing" into
    // "shifts disabled", so a waiter created without an explicit
    // toggle silently bypassed the start-shift gate and could ring up
    // orders with no shift attached.  For shift-supported roles,
    // undefined defaults to true; only an explicit `false` opts out.
    return correctRole && profile.useShifts !== false
  })

  return {
    shiftsSupported,
    shiftIsMissing,
    shiftShouldBeStarted,
    shiftShouldBeEnded,
    shiftIsHappening,
    shift,
  }
}
